/**
 * Bowtie Analytics Sidebar Component
 * Displays risk summary, effectiveness charts, and risk evolution
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PieChart, TrendingUp, Shield, AlertTriangle } from 'lucide-react';

// ==================== RISK SUMMARY PANEL ====================
export const RiskSummaryPanel = ({ bowtie }) => {
  const threatCount = bowtie?.threats?.length || 0;
  const consequenceCount = bowtie?.consequences?.length || 0;
  const barrierCount = bowtie?.barriers?.length || 0;
  const preventiveCount = bowtie?.barriers?.filter(b => b.barrier_type === 'preventive').length || 0;
  const recoveryCount = bowtie?.barriers?.filter(b => b.barrier_type === 'mitigating').length || 0;
  
  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          Risk Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-orange-900/30 rounded p-2 text-center">
            <div className="text-lg font-bold text-orange-400">{threatCount}</div>
            <div className="text-xs text-orange-300">Threats</div>
          </div>
          <div className="bg-red-900/30 rounded p-2 text-center">
            <div className="text-lg font-bold text-red-400">{consequenceCount}</div>
            <div className="text-xs text-red-300">Consequences</div>
          </div>
        </div>
        <div className="bg-slate-700/50 rounded p-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-slate-400">Total Controls</span>
            <span className="text-sm font-semibold text-emerald-400">{barrierCount}</span>
          </div>
          <div className="flex gap-2 text-xs">
            <Badge className="bg-blue-700/50 text-blue-200">{preventiveCount} Preventive</Badge>
            <Badge className="bg-purple-700/50 text-purple-200">{recoveryCount} Recovery</Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== EFFECTIVENESS CHART ====================
export const EffectivenessChart = ({ bowtie }) => {
  const barriers = bowtie?.barriers || [];
  const effectivenessCount = {
    high: barriers.filter(b => b.effectiveness === 'high').length,
    medium: barriers.filter(b => b.effectiveness === 'medium').length,
    low: barriers.filter(b => b.effectiveness === 'low').length,
    ineffective: barriers.filter(b => b.effectiveness === 'ineffective').length
  };
  
  const total = barriers.length || 1;
  const effectivePercent = Math.round(((effectivenessCount.high + effectivenessCount.medium * 0.6) / total) * 100);
  
  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
          <PieChart className="w-4 h-4 text-emerald-400" />
          Control Effectiveness
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Simple visual bar representation */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-emerald-500"></div>
            <span className="text-xs text-slate-400 flex-1">Effective</span>
            <span className="text-xs text-emerald-400 font-medium">{effectivenessCount.high}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-yellow-500"></div>
            <span className="text-xs text-slate-400 flex-1">Partial</span>
            <span className="text-xs text-yellow-400 font-medium">{effectivenessCount.medium}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-orange-500"></div>
            <span className="text-xs text-slate-400 flex-1">Weak</span>
            <span className="text-xs text-orange-400 font-medium">{effectivenessCount.low}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-red-500"></div>
            <span className="text-xs text-slate-400 flex-1">Non-effective</span>
            <span className="text-xs text-red-400 font-medium">{effectivenessCount.ineffective}</span>
          </div>
        </div>
        
        {/* Overall effectiveness meter */}
        <div className="mt-3 pt-3 border-t border-slate-700">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-slate-400">Overall Effectiveness</span>
            <span className={`text-sm font-bold ${effectivePercent >= 70 ? 'text-emerald-400' : effectivePercent >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
              {effectivePercent}%
            </span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all ${effectivePercent >= 70 ? 'bg-emerald-500' : effectivePercent >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
              style={{ width: `${effectivePercent}%` }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== RISK EVOLUTION PANEL ====================
export const RiskEvolutionPanel = ({ bowtie }) => {
  const inherentScore = bowtie?.inherent_risk_score || 0;
  const residualScore = bowtie?.residual_risk_score || 0;
  const reduction = inherentScore > 0 ? Math.round(((inherentScore - residualScore) / inherentScore) * 100) : 0;
  
  const getRiskColor = (score) => {
    if (score >= 20) return 'text-red-400';
    if (score >= 15) return 'text-orange-400';
    if (score >= 10) return 'text-yellow-400';
    if (score >= 5) return 'text-emerald-400';
    return 'text-slate-400';
  };
  
  const getRiskLabel = (score) => {
    if (score >= 20) return 'Critical';
    if (score >= 15) return 'High';
    if (score >= 10) return 'Medium';
    if (score >= 5) return 'Low';
    return 'Acceptable';
  };
  
  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-blue-400" />
          Risk Evolution
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between mb-3">
          <div className="text-center">
            <div className={`text-xl font-bold ${getRiskColor(inherentScore)}`}>{inherentScore}</div>
            <div className="text-xs text-slate-500">Inherent</div>
            <Badge className={`text-xs mt-1 ${inherentScore >= 15 ? 'bg-red-900/50 text-red-300' : 'bg-yellow-900/50 text-yellow-300'}`}>
              {getRiskLabel(inherentScore)}
            </Badge>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center gap-1 text-emerald-400">
              <span className="text-2xl">→</span>
              <span className="text-xs">-{reduction}%</span>
            </div>
          </div>
          <div className="text-center">
            <div className={`text-xl font-bold ${getRiskColor(residualScore)}`}>{residualScore}</div>
            <div className="text-xs text-slate-500">Residual</div>
            <Badge className={`text-xs mt-1 ${residualScore >= 15 ? 'bg-red-900/50 text-red-300' : residualScore >= 10 ? 'bg-yellow-900/50 text-yellow-300' : 'bg-emerald-900/50 text-emerald-300'}`}>
              {getRiskLabel(residualScore)}
            </Badge>
          </div>
        </div>
        
        {/* Visual bar comparison */}
        <div className="space-y-2">
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-500">Inherent Risk</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full ${inherentScore >= 15 ? 'bg-red-500' : 'bg-orange-500'}`}
                style={{ width: `${Math.min(inherentScore * 4, 100)}%` }}
              />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-500">Residual Risk</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full ${residualScore >= 15 ? 'bg-red-500' : residualScore >= 10 ? 'bg-yellow-500' : 'bg-emerald-500'}`}
                style={{ width: `${Math.min(residualScore * 4, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== COMBINED ANALYTICS SIDEBAR ====================
export const BowtieAnalyticsSidebar = ({ bowtie }) => {
  if (!bowtie) return null;
  
  return (
    <div className="w-64 flex-shrink-0 space-y-3 overflow-y-auto max-h-[calc(100vh-300px)]">
      <RiskSummaryPanel bowtie={bowtie} />
      <EffectivenessChart bowtie={bowtie} />
      <RiskEvolutionPanel bowtie={bowtie} />
    </div>
  );
};

export default BowtieAnalyticsSidebar;
